import { useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useUiStore, toDateStr } from '../../lib/uiStore';
import { useLang } from '../../lib/lang';
import { useErrMsg } from '../../lib/errors';
import { prettyAuditValue, shortDateTime } from '../../lib/time';
import type { AuditEntry } from '../../lib/types';

const ACTIONS = ['', 'create', 'update', 'reschedule', 'status_change', 'convert_pos', 'delete'];

const ACTION_COLOR: Record<string, string> = {
  create: '#16a34a', update: '#3b82f6', reschedule: '#6366f1', status_change: '#f59e0b', convert_pos: '#10b981', delete: '#ef4444',
};

const ACTION_TEXT: Record<string, { en: string; ar: string }> = {
  create: { en: 'create', ar: 'إنشاء' },
  update: { en: 'update', ar: 'تعديل' },
  reschedule: { en: 'reschedule', ar: 'إعادة جدولة' },
  status_change: { en: 'status change', ar: 'تغيير الحالة' },
  convert_pos: { en: 'convert pos', ar: 'تحويل لفاتورة' },
  delete: { en: 'delete', ar: 'حذف' },
};


export function AuditView(): JSX.Element {
  const { t } = useLang();
  const errMsg = useErrMsg();
  const openEdit = useUiStore((s) => s.openEdit);
  const [action, setAction] = useState('');
  // Default to today's activity — clear either field to widen the range.
  const [from, setFrom] = useState(() => toDateStr(new Date()));
  const [to, setTo] = useState(() => toDateStr(new Date()));
  const [sessionId, setSessionId] = useState('');
  const [page, setPage] = useState(1);
  const limit = 50;

  const actionText = (a: string): string => (ACTION_TEXT[a] ? t(ACTION_TEXT[a]!) : a.replace(/_/g, ' '));

  const q = useQuery({
    queryKey: ['audit', action, from, to, sessionId, page],
    queryFn: () => api.audit({
      action: action || undefined, from: from || undefined, to: to || undefined,
      sessionId: sessionId ? Number(sessionId) : undefined, page, limit,
    }),
    placeholderData: keepPreviousData,
  });

  const total = q.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / limit));
  const reset = (fn: () => void): void => { fn(); setPage(1); };

  return (
    <div className="appt-scroll h-full overflow-auto p-3 sm:p-4">
      <div className="mx-auto max-w-6xl space-y-3 sm:space-y-4">
        <div className="flex flex-wrap items-end gap-2 sm:gap-3">
          <h1 className="w-full text-lg font-bold text-slate-800 sm:w-auto sm:text-xl dark:text-slate-100">{t({ en: 'Audit log', ar: 'سجل التدقيق' })}</h1>
          <select className="appt-input min-w-[140px] flex-1 py-1.5 sm:w-auto sm:flex-none" value={action} onChange={(e) => reset(() => setAction(e.target.value))}>
            {ACTIONS.map((a) => <option key={a} value={a}>{a ? actionText(a) : t({ en: 'All actions', ar: 'كل الإجراءات' })}</option>)}
          </select>
          <input className="appt-input w-24 flex-none py-1.5" placeholder={t({ en: 'Appt #', ar: 'رقم الموعد' })} value={sessionId} onChange={(e) => reset(() => setSessionId(e.target.value.replace(/\D/g, '')))} />
          <label className="min-w-0 flex-1 text-xs text-slate-500 sm:flex-none">
            {t({ en: 'From', ar: 'من' })}
            <input type="date" className="appt-input py-1.5 sm:w-auto" value={from} onChange={(e) => reset(() => setFrom(e.target.value))} />
          </label>
          <label className="min-w-0 flex-1 text-xs text-slate-500 sm:flex-none">
            {t({ en: 'To', ar: 'إلى' })}
            <input type="date" className="appt-input py-1.5 sm:w-auto" value={to} onChange={(e) => reset(() => setTo(e.target.value))} />
          </label>
          <span className="ms-auto flex-none text-xs text-slate-400">{total} {t({ en: 'entries', ar: 'سجل' })}</span>
        </div>

        <div className="appt-card overflow-hidden">
          {/* See ReportsView: the min-width is what makes `overflow-x-auto`
              scroll instead of squashing the columns. */}
          <div className="appt-table-scroll">
            <table className="w-full min-w-[680px] text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800/50">
                <tr>
                  <th className="px-3 py-2 text-start">{t({ en: 'When', ar: 'الوقت' })}</th>
                  <th className="px-3 py-2 text-start">{t({ en: 'Action', ar: 'الإجراء' })}</th>
                  <th className="px-3 py-2 text-start">{t({ en: 'Details', ar: 'التفاصيل' })}</th>
                  <th className="px-3 py-2 text-start">{t({ en: 'User', ar: 'المستخدم' })}</th>
                  <th className="px-3 py-2 text-start">{t({ en: 'Appt', ar: 'الموعد' })}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {(q.data?.data ?? []).map((e) => <Row key={e.id} e={e} onOpen={openEdit} actionText={actionText} />)}
                {q.isLoading && (
                  <tr><td colSpan={5} className="px-3 py-10 text-center text-sm text-slate-400">{t({ en: 'Loading…', ar: 'جارٍ التحميل…' })}</td></tr>
                )}
                {q.isError && (
                  <tr><td colSpan={5} className="px-3 py-10 text-center text-sm text-red-600">
                    {errMsg(q.error, { en: 'Couldn’t load the audit log.', ar: 'تعذّر تحميل سجل التدقيق.' })}
                  </td></tr>
                )}
                {q.data && q.data.data.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-10 text-center text-sm text-slate-400">{t({ en: 'No audit entries for these filters.', ar: 'لا توجد سجلات لهذه المرشحات.' })}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-400">{t({ en: 'Page', ar: 'صفحة' })} {page} {t({ en: 'of', ar: 'من' })} {pages}</span>
          <div className="flex gap-2">
            <button className="appt-btn-ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>{t({ en: 'Prev', ar: 'السابق' })}</button>
            <button className="appt-btn-ghost" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>{t({ en: 'Next', ar: 'التالي' })}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ e, onOpen, actionText }: { e: AuditEntry; onOpen: (id: number) => void; actionText: (a: string) => string }): JSX.Element {
  const color = ACTION_COLOR[e.action] ?? '#64748b';
  return (
    <tr className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
      <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-500">{shortDateTime(e.created_at)}</td>
      <td className="px-3 py-2">
        <span className="rounded px-2 py-0.5 text-xs font-semibold" style={{ background: `${color}1a`, color }}>{actionText(e.action)}</span>
      </td>
      <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">
        {e.field_name && <span className="font-medium">{e.field_name}: </span>}
        {e.old_value != null && <span className="text-slate-400 line-through">{prettyAuditValue(e.old_value)}</span>}
        {e.old_value != null && e.new_value != null && <span className="mx-1">→</span>}
        {e.new_value != null && <span>{prettyAuditValue(e.new_value)}</span>}
        {e.old_value == null && e.new_value == null && <span className="text-slate-400">—</span>}
      </td>
      <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">{e.user_name ?? (e.user_id != null ? `#${e.user_id}` : '—')}</td>
      <td className="px-3 py-2 text-xs">
        {e.session_id != null ? (
          <button className="font-semibold text-brand hover:underline" onClick={() => onOpen(e.session_id!)}>#{e.session_id}</button>
        ) : '—'}
      </td>
    </tr>
  );
}
